import { useFrame, useThree } from '@react-three/fiber';
import React, {
  type MutableRefObject,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type PointLight,
  type ShaderMaterial,
  type Texture,
  BufferAttribute,
  BufferGeometry,
  Group,
  Sphere,
  TextureLoader,
  UniformsLib,
  UniformsUtils,
  Vector3,
} from 'three';

import { usePromiseStore } from 'state/promiseStore';
import type { GroupProps } from 'types/r3f';
import { publicAssets } from 'utils/gcs';

import fragmentShader from './cloud.frag';
import vertexShader from './cloud.vert';

const CLOUD_URL = publicAssets('/cloud.png');
const MAX_LIGHTS = 3; // Update the value also in the clouds.frag file
const textureLoader = new TextureLoader();

export interface StaticCloudProps extends GroupProps {
  /** How many segments or particles the cloud will have, default: 10 */
  segmentCount?: number;
  /** The box3 bounds of the cloud, default: (3, 3, 3) */
  bounds?: Vector3;
  /** The volume/thickness of the segments, default: 6 */
  volume?: number;
  /** Opacity, default: 1 */
  opacity?: number;
  pointLights?: MutableRefObject<PointLight>[];
  baseLight?: number;
  fog?: boolean;
}

export const StaticCloud = forwardRef<
  {
    position: Vector3;
    scale: number;
    opacity: number;
    userData: Record<string, any>;
    isLoaded: boolean;
  },
  StaticCloudProps
>(
  (
    {
      segmentCount = 10,
      bounds = new Vector3(3, 3, 3),
      volume = 6,
      opacity = 1,
      pointLights = [],
      baseLight = 1,
      fog = false,
      ...props
    },
    ref
  ) => {
    const exec = usePromiseStore(s => s.exec);
    const gl = useThree(state => state.gl);
    const groupRef = useRef<Group>(null!);
    const materialRef = useRef<ShaderMaterial>(null!);

    const [texture, setTexture] = useState<Texture | null>(null);

    useEffect(() => {
      exec(CLOUD_URL, () => textureLoader.loadAsync(CLOUD_URL)).then(tex => {
        setTexture(tex);
      });
    }, [exec]);

    const isLoadedRef = useRef(false);

    useEffect(() => {
      isLoadedRef.current = !!texture;
    }, [texture]);

    useImperativeHandle(
      ref,
      () => ({
        position: groupRef.current.position,
        userData: groupRef.current.userData,
        set scale(n: number) {
          const mat = materialRef.current;
          if (!mat) return;
          mat.uniforms.uScale.value = n;
        },
        get scale() {
          const mat = materialRef.current;
          if (!mat) return 0;
          return mat.uniforms.uScale.value;
        },
        set opacity(n: number) {
          const mat = materialRef.current;
          if (!mat) return;
          mat.uniforms.uOpacity.value = n;
        },
        get opacity() {
          const mat = materialRef.current;
          if (!mat) return 0;
          return mat.uniforms.uOpacity.value;
        },
        get isLoaded() {
          return isLoadedRef.current;
        },
      }),
      []
    );

    const geometry = useMemo(() => {
      const bg = new BufferGeometry();
      bg.boundingSphere = new Sphere(
        new Vector3(0, 0, 0),
        Math.max(bounds.x, bounds.y, bounds.z) + volume / 2
      );

      const positions = new Float32Array(segmentCount * 3);
      const sizes = new Float32Array(segmentCount);

      for (let i = 0; i < segmentCount; i++) {
        const i3 = i * 3;
        positions[i3 + 0] = bounds.x * (Math.random() * 2 - 1);
        positions[i3 + 1] = bounds.y * (Math.random() * 2 - 1);
        positions[i3 + 2] = bounds.z * (Math.random() * 2 - 1);
        sizes[i] = Math.max(0.2, Math.random()) * volume * gl.getPixelRatio();
      }

      bg.setAttribute('position', new BufferAttribute(positions, 3));
      bg.setAttribute('aSize', new BufferAttribute(sizes, 1));
      return bg;
    }, [gl, segmentCount, bounds, volume]);

    useFrame(() => {
      if (!materialRef.current) return;

      for (let i = 0; i < Math.min(pointLights.length, MAX_LIGHTS); i++) {
        const mat = materialRef.current;
        const { x: lx, y: ly, z: lz } = pointLights[i].current.position;
        const intensity = pointLights[i].current.intensity;

        const i3 = i * 3;
        mat.uniforms.uLightPositions.value[i3] = lx;
        mat.uniforms.uLightPositions.value[i3 + 1] = ly;
        mat.uniforms.uLightPositions.value[i3 + 2] = lz;
        mat.uniforms.uLightIntensities.value[i] = intensity;
      }
    });

    return (
      <group ref={groupRef} {...props}>
        {texture && (
          <points geometry={geometry}>
            <shaderMaterial
              ref={materialRef}
              vertexShader={vertexShader}
              fragmentShader={fragmentShader}
              uniforms={UniformsUtils.merge([
                UniformsLib['fog'],
                {
                  uTexture: { value: texture },
                  uOpacity: { value: opacity },
                  uBaseLight: { value: baseLight },
                  uLightPositions: { value: new Float32Array(MAX_LIGHTS * 3) },
                  uLightIntensities: { value: new Float32Array(MAX_LIGHTS) },
                  uNumLights: {
                    value: Math.min(pointLights.length, MAX_LIGHTS),
                  },
                  uScale: { value: 1 },
                },
              ])}
              transparent={true}
              depthWrite={false}
              fog={fog}
            />
          </points>
        )}
      </group>
    );
  }
);
StaticCloud.displayName = 'StaticCloud';
